import type { IntlShape } from 'react-intl';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ShowDiffPlugin } from '../../showDiffPluginType';
import { ContributorTagController } from '../../ui/ContributorTag/contributorTagController';

import { clampAnchorPosIntoCell } from './createAnchorDecorationWidgets';
import { buildContributorTagDecorationSpec } from './decorationKeys';

/** Marks the host element of one tag, so a tag can be found in the document by its own diff. */
export const CONTRIBUTOR_TAG_HOST_ATTRIBUTE = 'data-contributor-tag-host';

// Contributor tags only render behind this gate — see `areAttributionColorGatesEnabled`.
const isContributorTagWidgetEnabled = (): boolean =>
	fg('confluence_ncs_step_diffing_version_history');

/**
 * Everything a tag needs to draw itself into the document, plumbed down from the plugin. Absent in
 * unit tests that build decorations directly: the hosts are still emitted, they just stay empty.
 */
export type ContributorTagMountContext = {
	api?: ExtractInjectionAPI<ShowDiffPlugin>;
	/** This editor's own content root — see `ContributorTagControllerOptions.getEditorRoot`. */
	getEditorRoot: () => HTMLElement | null;
	getIntl: () => IntlShape;
};

/** A mounted tag, so whoever emitted the host can take it down again. */
export type ContributorTagMount = {
	destroy: () => void;
};

/**
 * Draws the tag into `host`. The tag resolves its own model from plugin state, so a redraw of the
 * host re-parents the tag instead of stranding it, and `isActive` changes arrive as an update.
 *
 * Each mount owns its own controller and DOM, so when ProseMirror draws a replacement widget before
 * destroying the one it replaces, the outgoing `destroy` cannot take the incoming tag down with it
 * (EDITOR-8702).
 */
export const mountContributorTag = ({
	diffId,
	host,
	mountContext,
}: {
	diffId: string;
	host: HTMLElement;
	mountContext: ContributorTagMountContext | undefined;
}): ContributorTagMount | undefined => {
	if (!mountContext) {
		return undefined;
	}

	const controller = new ContributorTagController({ ...mountContext, diffId });
	controller.mount(host);

	return controller;
};

export const unmountContributorTag = (mount: ContributorTagMount | undefined): void => {
	mount?.destroy();
};

/**
 * A zero-size inline host for one contributor tag. `position: relative` makes it the containing
 * block of the absolutely positioned tag, so the tag sits on the change with no measurement.
 */
const buildContributorTagHost = (diffId: string): HTMLSpanElement => {
	const host = document.createElement('span');
	host.setAttribute(CONTRIBUTOR_TAG_HOST_ATTRIBUTE, diffId);
	host.style.setProperty('position', 'relative');
	return host;
};

/**
 * The host for a tag on deleted content, which renders inside its own widget decoration rather than
 * needing one of its own. Unmount with `unmountContributorTag` from that widget's `destroy`.
 */
export const createContributorTagHost = (
	diffId: string,
	mountContext?: ContributorTagMountContext,
): { host: HTMLSpanElement; mount: ContributorTagMount | undefined } | undefined => {
	if (!isContributorTagWidgetEnabled()) {
		return undefined;
	}

	const host = buildContributorTagHost(diffId);

	return { host, mount: mountContributorTag({ diffId, host, mountContext }) };
};

/**
 * The start of the inline content that `[from, to)` actually highlights.
 *
 * `from` is not sufficient: a range can begin at the tail of one paragraph and only cover text in a
 * later block, and ProseMirror clips `Decoration.inline` to the content it can paint — so a host
 * left at `from` would sit lines away from the highlight.
 */
const resolveInlineContentStart = (doc: PMNode, from: number, to: number): number => {
	let edge: number | undefined;

	doc.nodesBetween(from, to, (node, nodePos) => {
		if (!node.isTextblock) {
			return edge === undefined;
		}

		// The slice of this block the range actually covers; empty means nothing paintable.
		const contentFrom = Math.max(nodePos + 1, from);
		const contentTo = Math.min(nodePos + 1 + node.content.size, to);
		if (contentTo > contentFrom && edge === undefined) {
			edge = contentFrom;
		}

		// Textblocks have no block children to descend into.
		return false;
	});

	return edge ?? from;
};

/**
 * A widget hosting the contributor tag of one inline diff range, placed on the first character the
 * range highlights.
 *
 * The tag is mounted in `toDOM` and taken down in `destroy`, so it lives exactly as long as the host
 * element ProseMirror drew it into — see `mountContributorTag`.
 */
export const createContributorTagWidget = ({
	doc,
	from,
	to,
	diffId,
	mountContext,
}: {
	diffId: string;
	doc: PMNode;
	from: number;
	mountContext?: ContributorTagMountContext;
	to: number;
}): Decoration | undefined => {
	if (!isContributorTagWidgetEnabled()) {
		return undefined;
	}

	// Reassigned when ProseMirror redraws this decoration, which it may do more than once for the
	// same `Decoration` instance.
	let mount: ContributorTagMount | undefined;

	return Decoration.widget(
		// Keep the host out of the table row's grid (EDITOR-8442).
		clampAnchorPosIntoCell(doc, resolveInlineContentStart(doc, from, to), 1),
		() => {
			const host = buildContributorTagHost(diffId);
			mount = mountContributorTag({ diffId, host, mountContext });
			return host;
		},
		{
			...buildContributorTagDecorationSpec(diffId),
			side: 1,
			// The tag is chrome: it inherits no surrounding mark, and its own events are not the
			// document's to handle.
			marks: [],
			ignoreSelection: true,
			stopEvent: () => true,
			destroy: () => {
				unmountContributorTag(mount);
				mount = undefined;
			},
		},
	);
};
